import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { InteractiveContainer } from "@jugl-web/ui-components";
import { useTranslations } from "@jugl-web/utils";
import { Portal } from "@headlessui/react";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import ghostImage from "./assets/ghost.png";
import {
  OnboardingFlowsContext,
  OnboardingFlowsContextValue,
  OnboardingFlowStep,
  OnboardingFlowId,
} from "./OnboardingFlowsContext";

type PendingFlow =
  | { flow: "postPurchase" }
  | { flow: "planUpgraded"; planName: string }
  | { flow: "planDowngraded"; planName: string; previousPlanName: string };

export const OnboardingFlowsProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslations();
  const { isSubscriptionCanceled, isLoading: isSubLoading } =
    useSubscriptionV2();
  const [activeFlow, setActiveFlow] = useState<OnboardingFlowId | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [upgradedPlanName, setUpgradedPlanName] = useState<string>("");
  const [previousPlanName, setPreviousPlanName] = useState<string>("");

  // Auto-start flow from sessionStorage (survives hard reload)
  useEffect(() => {
    if (isSubLoading) return;
    const pending = sessionStorage.getItem("onboarding_flow_pending");
    if (!pending) return;
    sessionStorage.removeItem("onboarding_flow_pending");

    // Don't run if subscription is not active
    if (isSubscriptionCanceled) return;

    try {
      const parsed = JSON.parse(pending) as PendingFlow;
      if (parsed.flow === "planUpgraded") {
        setUpgradedPlanName(parsed.planName);
      }
      if (parsed.flow === "planDowngraded") {
        setUpgradedPlanName(parsed.planName);
        setPreviousPlanName(parsed.previousPlanName);
      }
      setActiveFlow(parsed.flow);
      setActiveStep(0);
    } catch {
      // Legacy format: plain string
      setActiveFlow(pending as OnboardingFlowId);
      setActiveStep(0);
    }
  }, [isSubLoading, isSubscriptionCanceled]);

  const flowSteps: Record<OnboardingFlowId, OnboardingFlowStep[]> = useMemo(
    () => ({
      postPurchase: [
        {
          type: "tooltip",
          title: t({
            id: "onboarding-flows.post-purchase.credits-title",
            defaultMessage: "Plan was successfully activated!",
          }),
          text: t({
            id: "onboarding-flows.post-purchase.credits-text",
            defaultMessage:
              "Track your Credits usage here, manage any time later on \ud83d\ude4c",
          }),
        },
        {
          type: "tooltip",
          title: t({
            id: "onboarding-flows.post-purchase.settings-title",
            defaultMessage: "Additional Items",
          }),
          text: t({
            id: "onboarding-flows.post-purchase.additional-items",

            defaultMessage:
              "Find general settings, help resources and other additional items here ✨",
          }),
        },
      ],
      planUpgraded: [],
      planDowngraded: [],
    }),
    [t]
  );

  const runFlow = useCallback((flowId: OnboardingFlowId) => {
    setActiveFlow(flowId);
    setActiveStep(0);
  }, []);

  const nextStep = useCallback(() => {
    if (!activeFlow) return;
    const steps = flowSteps[activeFlow];
    if (!steps || steps.length === 0) {
      setActiveFlow(null);
      setActiveStep(0);
      return;
    }
    const currentStep = steps[activeStep];
    currentStep?.onNext?.();

    if (activeStep + 1 >= steps.length) {
      setActiveFlow(null);
      setActiveStep(0);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  }, [activeFlow, activeStep, flowSteps]);

  const dismissFlow = useCallback(() => {
    setActiveFlow(null);
    setActiveStep(0);
  }, []);

  const isStepActive = useCallback(
    (flowId: OnboardingFlowId, step: number) =>
      activeFlow === flowId && activeStep === step,
    [activeFlow, activeStep]
  );

  const getStep = useCallback(
    (flowId: OnboardingFlowId, step: number) => flowSteps[flowId]?.[step],
    [flowSteps]
  );

  const getTotalSteps = useCallback(
    (flowId: OnboardingFlowId) => flowSteps[flowId]?.length ?? 0,
    [flowSteps]
  );

  const value: OnboardingFlowsContextValue = useMemo(
    () => ({
      activeFlow,
      activeStep,
      runFlow,
      nextStep,
      dismissFlow,
      isStepActive,
      getStep,
      getTotalSteps,
    }),
    [
      activeFlow,
      activeStep,
      runFlow,
      nextStep,
      dismissFlow,
      isStepActive,
      getStep,
      getTotalSteps,
    ]
  );

  return (
    <OnboardingFlowsContext.Provider value={value}>
      {children}
      {activeFlow === "planUpgraded" && (
        <Portal>
          <div className="fixed bottom-10 right-10 z-40 w-[360px] rounded-[20px] bg-[#333333]">
            <div className="border-dark-800 flex items-center justify-between gap-2 border-0 border-b border-solid p-6 pb-0">
              <span className="font-secondary text-lg font-semibold leading-[140%] text-white">
                {t(
                  {
                    id: "onboarding-flows.plan-upgraded.title",
                    defaultMessage:
                      "Workspace was upgraded to {planName} Plan!",
                  },
                  { planName: upgradedPlanName }
                )}
              </span>
              <img
                src={ghostImage}
                alt=""
                className="pointer-events-none relative h-[60px] translate-x-[25px] translate-y-[0.5px]"
              />
            </div>
            <div className="py-2.5 px-6">
              <span className="font-secondary text-sm text-white">
                {t(
                  {
                    id: "onboarding-flows.plan-upgraded.text",
                    defaultMessage:
                      "Enjoy the benefits of a {planName} Plan features! \ud83d\ude4c",
                  },
                  { planName: upgradedPlanName }
                )}
              </span>
            </div>
            <div className="mx-6 mb-6 flex h-10 items-center justify-end px-2">
              <InteractiveContainer
                className="font-secondary bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text text-sm font-semibold uppercase leading-3 text-transparent"
                onClick={dismissFlow}
              >
                {t({ id: "common.got-it", defaultMessage: "Got it" })}
              </InteractiveContainer>
            </div>
          </div>
        </Portal>
      )}
      {activeFlow === "planDowngraded" && (
        <Portal>
          <div className="fixed bottom-10 right-10 z-40 w-[360px] rounded-[20px] bg-[#333333]">
            <div className="border-dark-800 flex items-center justify-between gap-2 border-0 border-b border-solid p-6 pb-0">
              <span className="font-secondary text-lg font-semibold leading-[140%] text-white">
                {t(
                  {
                    id: "onboarding-flows.plan-downgraded.title",
                    defaultMessage:
                      "Workspace was downgraded to {planName} Plan",
                  },
                  { planName: upgradedPlanName }
                )}
              </span>
              <img
                src={ghostImage}
                alt=""
                className="pointer-events-none relative h-[60px] translate-x-[25px] translate-y-[0.5px]"
              />
            </div>
            <div className="py-2.5 px-6">
              <span className="font-secondary text-sm text-white">
                {t(
                  {
                    id: "onboarding-flows.plan-downgraded.text",
                    defaultMessage:
                      "Features & Add-ons of the {previousPlanName} Plan is not available anymore",
                  },
                  { previousPlanName }
                )}
              </span>
            </div>
            <div className="mx-6 mb-6 flex h-10 items-center justify-end px-2">
              <InteractiveContainer
                className="font-secondary bg-[linear-gradient(149deg,#A9BDFF_68.46%,#F79FFF_93.9%)] bg-clip-text text-sm font-semibold uppercase leading-3 text-transparent"
                onClick={dismissFlow}
              >
                {t({ id: "common.got-it", defaultMessage: "Got it" })}
              </InteractiveContainer>
            </div>
          </div>
        </Portal>
      )}
    </OnboardingFlowsContext.Provider>
  );
};
